import { Localization } from '@mui/x-data-grid-pro/internals'
import { enUS, esES, frFR, nlNL } from '@mui/x-data-grid-pro/locales'
import {
  enUS as muiDateEnUS,
  esES as muiDateEsES,
  frFR as muiDateFrFR,
  nlNL as muiDateNlNL
} from '@mui/x-date-pickers/locales'
import { enGB, es, fr, Locale, nlBE } from 'date-fns/locale'
import { CountryCode } from 'libphonenumber-js'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo
} from 'react'
import { match } from 'ts-pattern'
import { SupportedLanguage } from 'domain/models/Translations/Translation'
import { Opts } from 'helpers/AdapterPlainDateTimeTemporal'
import { apiClient } from 'services/api/client'

// source: https://cdn.jsdelivr.net/npm/@huncuska/cio-country-codes@1.1.0/src/lib/cio-country-codes.js
import countryCodes from './countryCodes.json'
import {
  temporalEnLocale,
  temporalEsLocale,
  temporalFrLocale,
  temporalNlLocale
} from './temporal.locales'

const defaultLocale = 'fr'
const defaultCountryCode = defaultLocale.toUpperCase() as CountryCode

export interface Country {
  code: string
  code2: CountryCode
  name: string
}

interface LocaleContext {
  saveLocale: (newLocale: string) => void
  fnsLocale: Locale
  dateStringLocale: string
  selectedMuiGridLocale: Localization
  selectedMuiDateLocale: typeof muiDateFrFR
  countryCode: CountryCode
  orderedCountries: Country[]
  findCountryNameByCode: (code: string | null) => string | undefined
  temporalLocale: Opts['locale']
}

export const LocaleContext = createContext<LocaleContext>({
  saveLocale: () => {},
  fnsLocale: fr,
  dateStringLocale: 'fr-FR',
  selectedMuiGridLocale: frFR,
  selectedMuiDateLocale: muiDateFrFR,
  countryCode: defaultCountryCode,
  orderedCountries: [],
  findCountryNameByCode: () => undefined,
  temporalLocale: temporalFrLocale
})

export const LocaleProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation()
  const router = useRouter()
  const session = useSession()

  const saveLocale = (newLocale: string) => {
    if (router.locale === newLocale) {
      return
    }
    if (session.status === 'authenticated') {
      apiClient.post('user/save-language', {
        language: newLocale,
        userId: session.data.user.id
      })
    }
    const { pathname, asPath, query } = router
    router.push({ pathname, query }, asPath, { locale: newLocale })
  }

  useEffect(() => {
    const currentLocale = router.locale ?? defaultLocale
    saveLocale(currentLocale)
  }, [])

  const [
    fnsLocale,
    dateStringLocale,
    selectedMuiGridLocale,
    selectedMuiDateLocale,
    regionLocale,
    temporalLocale
  ] = match<
    SupportedLanguage,
    [
      Locale,
      string,
      Localization,
      typeof muiDateFrFR,
      Intl.DisplayNames,
      Opts['locale']
    ]
  >(i18n.language as SupportedLanguage)
    .with('en', () => [
      enGB,
      'en-GB',
      enUS,
      muiDateEnUS,
      new Intl.DisplayNames(['en'], { type: 'region' }),
      temporalEnLocale
    ])
    .with('es', () => [
      es,
      'es-ES',
      esES,
      muiDateEsES,
      new Intl.DisplayNames(['es'], { type: 'region' }),
      temporalEsLocale
    ])
    .with('nl', () => [
      nlBE,
      'nl-BE',
      nlNL,
      muiDateNlNL,
      new Intl.DisplayNames(['nl'], { type: 'region' }),
      temporalNlLocale
    ])
    .with('fr', () => [
      fr,
      'fr-FR',
      frFR,
      muiDateFrFR,
      new Intl.DisplayNames(['fr'], { type: 'region' }),
      temporalFrLocale
    ])
    .exhaustive()

  const countriesList = countryCodes.map(({ ISO2, CIO }) => ({
    code: CIO,
    code2: ISO2 as CountryCode,
    name: regionLocale.of(ISO2) ?? 'unknown'
  }))

  const countryCode = useMemo(
    () =>
      (navigator.languages
        .filter((language) => language.includes('-'))[0]
        ?.split('-')[1] as CountryCode) ?? defaultCountryCode,
    []
  )

  const orderedCountries = useMemo(
    () =>
      countriesList.sort((a, b) => {
        if (a.code2 === countryCode && b.code2 !== countryCode) return -1
        if (a.code2 !== countryCode && b.code2 === countryCode) return 1
        return a.name.localeCompare(b.name)
      }),
    [countryCode, countriesList]
  )

  const findCountryNameByCode = useCallback(
    (code: string | null) => {
      return code
        ? countriesList.find((country) => country.code === code)?.name
        : undefined
    },
    [countriesList]
  )

  return (
    <LocaleContext.Provider
      value={{
        saveLocale,
        fnsLocale,
        dateStringLocale,
        selectedMuiGridLocale,
        selectedMuiDateLocale,
        countryCode,
        orderedCountries,
        findCountryNameByCode,
        temporalLocale
      }}
    >
      {children}
    </LocaleContext.Provider>
  )
}
